import React from 'react';
import { cn } from '@/lib/utils';
import Text from './Text';

/**
 * The pill tab row from the reference — "All orders 152 · In progress 54 ·
 * Completed 77". Active is UI green (this is a *selection*, not a call to
 * action), and every tab carries its count so a vendor can see the shape of
 * their data before clicking into it.
 */

export interface FilterTabItem {
  key: string;
  label: React.ReactNode;
  count?: number;
}

export interface FilterTabsProps {
  items: FilterTabItem[];
  value?: string;
  onChange?: (key: string) => void;
  className?: string;
}

const FilterTabs: React.FC<FilterTabsProps> = ({ items, value, onChange, className }) => (
  <div role="tablist" className={cn('flup-rail gap-2', className)}>
    {items.map((item) => {
      const active = item.key === value;
      return (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange?.(item.key)}
          className={cn(
            'inline-flex items-center gap-2 h-9 px-3.5 rounded-flup-sm transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flup-brand focus-visible:ring-offset-1',
            active
              ? 'bg-flup-green text-white'
              : 'bg-flup-surface-alt text-flup-ink-2 hover:bg-flup-sunken hover:text-flup-ink',
          )}
        >
          <Text variant="labelSemi" tone="inherit" as="span">
            {item.label}
          </Text>
          {typeof item.count === 'number' ? (
            <span
              className={cn(
                'inline-grid place-items-center min-w-[22px] h-5 px-1.5 rounded-flup-xs nums',
                active ? 'bg-white/20 text-white' : 'bg-flup-surface text-flup-ink-3',
              )}
            >
              <Text variant="micro" tone="inherit" as="span">
                {item.count}
              </Text>
            </span>
          ) : null}
        </button>
      );
    })}
  </div>
);

export default React.memo(FilterTabs);
